/**
 * viewportPlot — PDF-3 CORE: render MODEL-space geometry + text through each
 * paper-space VIEWPORT, clipped to the VP rectangle, at the per-VP scale/twist.
 *
 * # What this module owns
 * Given a VIEWPORT entity, the document, the page transform (paper-mm → pt) and
 * the convert context, it produces the VP's clipped content:
 *   - the COMPOSED transform `vpTransform(mx,my) = pageTransform(modelToPaper(
 *     {x:mx,y:my}, vpLike, paperOrigin))` — i.e. model→paper (the reused VP
 *     center/scale/twist affine, NOT `buildViewportModelCamera`) then the
 *     existing page transform (PLAN-plot-pdf.md §4 "Per-VP вложенный");
 *   - the model-space path-primitives + text-run-primitives (RAW model coords),
 *     filtered by the per-VP visibility predicate, so the caller can emit them
 *     THROUGH `vpTransform` inside the VP clip;
 *   - the VP clip rectangle in PAPER-mm corners (center ± width/2, height/2) so
 *     the caller maps them via `pageTransform` and emits `… W n`. The VP frame
 *     on the sheet is axis-aligned (twist only affects the model view, per
 *     `pointInViewport`'s contract), so a rectangle clip is correct.
 *
 * # Control-VP / VP-off detection (cited from the model)
 *   - **Control VP**: DXF group 69 (`viewportId`) value `1` is the paper-space
 *     pseudo-viewport (covers the whole sheet, not a model window). The
 *     project's importer faithfully includes it (model.ts:746 documents
 *     `viewportId === 1` as the control VP; dxfBridge.layouts.test.ts filters
 *     it the same way). We never emit model content for it.
 *   - **VP off**: the parsed scalar `viewportStatus` (DXF group 68) is the
 *     viewport's stacking order — a **positive** value (1, 2, 3 …) is an ON
 *     viewport's stack position; **`<= 0` means the VP is off / not active**.
 *     This MATCHES the project's own renderer, which activates a viewport iff
 *     `viewportStatus > 0` (Scene3DStage.tsx:1167; viewportRenderPass.test.ts
 *     excludes status 0 "off" and status -1 "not-active"). We skip the whole
 *     VP when `viewportStatus <= 0`. A degenerate VP (height ≤ 0 or viewHeight
 *     ≤ 0) is also skipped (no model mapping — `viewHeight/height` is NaN/∞).
 *
 *     NOTE on the control VP: the paper-space control VP carries
 *     `viewportStatus === 1` (it is "on"), so the off-rule alone would NOT skip
 *     it — the `viewportId === 1` control-VP check below is what excludes it.
 *
 * # Annotative-per-VP (PDF-3b — documented deferral, NOT faked)
 * AutoCAD sizes annotative content (TEXT/DIM/MLEADER/HATCH) at a FIXED paper
 * height = base height × the VP's annotation scale, choosing the annotation
 * representation matching the VP scale (see PLAN §2 "Аннотативность"). The
 * current IR (`text-run`) carries the raw model height and the model shape does
 * not expose a resolved "annotative base paper height + per-scale context" the
 * exporter can size from without a full cannoscale resolver. PDF-3 implements
 * the NON-annotative path fully (geometry + text scale with the VP like any
 * other model content); annotative-per-VP paper-sizing is deferred to PDF-3b.
 * See {@link AnnotativeSizingNote} for the in-code issue note.
 */

import type {
  DrawingDocument,
  DrawingEntity,
  LayerDefinition,
  ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  modelToPaper,
  type PaperOrigin,
  type ViewportLike,
} from "../../scene3d/viewports/viewportTransform2d";
import type { PdfTransform } from "./pdfTransform";
import {
  type CadConvertContext,
  type GradientHatchPrimitive,
  type HatchPatternPrimitive,
  type PathPrimitive,
  type TextRunPrimitive,
} from "./cadPrimitive";
import { entityToPrimitives } from "./entityToPrimitives";

/**
 * PDF-3b deferral marker (documented incomplete subsystem, CLAUDE.md allows a
 * known gap with a completion plan + issue note — NOT a silent drop / fake).
 *
 * Annotative-per-VP paper-sizing: model entities flagged annotative should be
 * sized at a fixed paper height = base × the VP `viewportCannoscaleFactor`,
 * picking the annotation-scale representation matching the VP. The non-
 * annotative path below is complete; annotative sizing is PDF-3b.
 */
export const AnnotativeSizingNote =
  "PDF-3b: annotative-per-VP paper-sizing deferred (non-annotative path complete)";

/** A point in PAPER-space mm (the VP clip rectangle is expressed here). */
export interface PaperPointMm {
  x: number;
  y: number;
}

/** The four corners (paper-mm) of a viewport's axis-aligned clip rectangle. */
export interface VpClipRectMm {
  /** Lower-left, lower-right, upper-right, upper-left (CCW). */
  corners: [PaperPointMm, PaperPointMm, PaperPointMm, PaperPointMm];
}

/** Resolved layer-visibility record for the per-VP predicate. */
interface LayerVisibility {
  visible: boolean;
  frozen: boolean;
  plottable: boolean;
}

/** name(lower-case) → visibility record. */
export type LayerVisibilityMap = ReadonlyMap<string, LayerVisibility>;

/** Build a case-insensitive layer visibility lookup from the layer table. */
export function buildLayerVisibilityMap(
  layers: readonly LayerDefinition[],
): LayerVisibilityMap {
  const map = new Map<string, LayerVisibility>();
  for (const l of layers) {
    map.set(l.name.toLowerCase(), {
      visible: l.visible !== false,
      frozen: l.frozen === true,
      plottable: l.plottable !== false,
    });
  }
  return map;
}

/**
 * Per-VP visibility predicate (shared helper). An entity on `layerName` is
 * plotted in a VP iff:
 *   - its layer is NOT (visible === false / frozen === true / plottable ===
 *     false) — GLOBAL layer plot-visibility, and
 *   - the layer name is NOT in the VP's `frozenLayers` set (per-VP freeze).
 *
 * A layer absent from the table is treated as visible/on/plottable (AutoCAD
 * default for an undefined layer; the entity still plots).
 */
export function isEntityVisibleInVp(
  layerName: string,
  layers: LayerVisibilityMap,
  vpFrozen: ReadonlySet<string>,
): boolean {
  const key = layerName.toLowerCase();
  const rec = layers.get(key);
  if (rec && (!rec.visible || rec.frozen || !rec.plottable)) return false;
  if (vpFrozen.has(key)) return false;
  return true;
}

/**
 * Paper-space visibility predicate (PDF-3 §5 — correct-behaviour addition over
 * PDF-1, which did not filter paper-space entities by layer). The SAME global
 * layer off/frozen/plottable rule applies to paper-space entities; `frozenLayers`
 * (per-VP) does NOT apply to paper space.
 */
export function isPaperEntityVisible(
  layerName: string,
  layers: LayerVisibilityMap,
): boolean {
  const rec = layers.get(layerName.toLowerCase());
  if (rec && (!rec.visible || rec.frozen || !rec.plottable)) return false;
  return true;
}

/** Whether a VIEWPORT is the paper-space control VP (DXF group 69 == 1). */
export function isControlViewport(vp: ViewportEntity): boolean {
  return vp.viewportId === 1;
}

/**
 * Whether a VIEWPORT is OFF or degenerate and contributes no model content.
 *   - `viewportStatus <= 0` → OFF / not-active (matches the project renderer's
 *     `viewportStatus > 0` activation rule, Scene3DStage.tsx:1167).
 *   - `height <= 0` / `viewHeight <= 0` → degenerate (no model mapping).
 */
export function isViewportOffOrDegenerate(vp: ViewportEntity): boolean {
  if (typeof vp.viewportStatus === "number" && vp.viewportStatus <= 0) {
    return true;
  }
  if (!(vp.height > 0) || !(vp.viewHeight > 0)) return true;
  return false;
}

/**
 * Project a {@link ViewportEntity} onto the pure-affine {@link ViewportLike}.
 *
 * `viewTarget` is carried through DELIBERATELY: `modelToPaper` anchors the VP
 * window on `viewTarget + R(viewTwistAngle)·viewCenter` (12/22 are DCS, whose
 * axes the twist turns; 17/27 is the WCS
 * point the DCS origin sits at — see `drawing-model`'s `viewportWcsViewCenter`),
 * which is the SAME anchor the on-screen per-VP camera uses. Dropping it in
 * this synthetic literal would plot a sheet that disagrees with the screen by
 * the whole target vector on any viewport whose target is non-zero.
 */
export function toViewportLike(vp: ViewportEntity): ViewportLike {
  return {
    center: { x: vp.center.x, y: vp.center.y },
    width: vp.width,
    height: vp.height,
    viewCenter: { x: vp.viewCenter.x, y: vp.viewCenter.y },
    viewTarget: { x: vp.viewTarget.x, y: vp.viewTarget.y },
    viewHeight: vp.viewHeight,
    viewTwistAngle: vp.viewTwistAngle,
  };
}

/**
 * Compose the VP transform: `model-mm → paper-mm` (the reused VP center/scale/
 * twist affine) → `paper-mm → pt` (the page transform). Returns a closure with
 * the SAME signature as {@link PdfTransform} so the caller can feed it to
 * `emitBatchedPaths` / `paintTextRuns` unchanged.
 *
 * `paperOrigin` defaults to {0,0}: the VP `center` is already authored in
 * layout/paper mm, so no extra origin offset is applied (a layout that carried
 * a paper-space origin offset would supply it here; none is exposed today).
 */
export function buildVpTransform(
  vp: ViewportEntity,
  pageTransform: PdfTransform,
  paperOrigin: PaperOrigin = { x: 0, y: 0 },
): PdfTransform {
  const vpLike = toViewportLike(vp);
  return (mx: number, my: number) => {
    const paper = modelToPaper({ x: mx, y: my }, vpLike, paperOrigin);
    return pageTransform(paper.x, paper.y);
  };
}

/**
 * The VP's axis-aligned clip rectangle in PAPER-mm (center ± half-extent). The
 * caller maps each corner through the page transform and emits a `re … W n`
 * (or a 4-point `m/l/l/l/h … W n`) clip. `paperOrigin` is added to the centre
 * (same convention as {@link buildVpTransform}).
 */
export function vpClipRectMm(
  vp: ViewportEntity,
  paperOrigin: PaperOrigin = { x: 0, y: 0 },
): VpClipRectMm {
  const cx = vp.center.x + paperOrigin.x;
  const cy = vp.center.y + paperOrigin.y;
  const hw = vp.width / 2;
  const hh = vp.height / 2;
  return {
    corners: [
      { x: cx - hw, y: cy - hh }, // lower-left
      { x: cx + hw, y: cy - hh }, // lower-right
      { x: cx + hw, y: cy + hh }, // upper-right
      { x: cx - hw, y: cy + hh }, // upper-left
    ],
  };
}

/** Result of extracting a viewport's model content. */
export interface ViewportContent {
  /** Model-space stroked paths (RAW model coords; emit through vpTransform). */
  paths: PathPrimitive[];
  /** Model-space text runs (RAW model coords; emit through vpTransform). */
  textRuns: TextRunPrimitive[];
  /**
   * Model-space PATTERN-fill hatches (PDF-5a; RAW model coords). Each is its
   * own clip+strokes block emitted through `vpTransform` inside the VP clip.
   */
  hatchPatterns: HatchPatternPrimitive[];
  /**
   * Model-space GRADIENT-fill hatches (PDF-6.5b; RAW model coords). Each is its
   * own clip + `sh` shading block emitted through `vpTransform` inside the VP
   * clip (same dispatch shape as {@link hatchPatterns}).
   */
  gradientHatches: GradientHatchPrimitive[];
  /** Clip rectangle in paper-mm. */
  clipRectMm: VpClipRectMm;
  /** The composed model→pt transform for this VP. */
  transform: PdfTransform;
  /**
   * The VP's model→paper LINEAR scale (`vp.height / vp.viewHeight`). Text runs
   * carry MODEL-unit heights; the caller multiplies by this so the glyph SIZE
   * scales with the VP (the composed `transform` only scales the anchor
   * position, not the scalar font size). 0 when the VP is skipped (unused).
   */
  unitToPaperScale: number;
  /**
   * The VP's view-twist angle in degrees (`vp.viewTwistAngle`). `modelToPaper`
   * rotates the model view by −twist, so model-axis-aligned text appears
   * rotated by −twist on paper; the caller passes `−twistDeg` as the text
   * extra rotation. 0 when the VP is skipped (unused).
   */
  twistDeg: number;
  /** True when the VP is control / off / degenerate and yields no content. */
  skipped: boolean;
  /** Skip reason for diagnostics ("control" | "off" | null). */
  skipReason: "control" | "off" | null;
}

/**
 * Resolve the document's MODEL space (the single `kind === "model"` space, or
 * the first space as a fallback for malformed docs).
 */
export function resolveModelSpace(doc: DrawingDocument): {
  entities: readonly DrawingEntity[];
} {
  const model = doc.spaces.find((s) => s.kind === "model");
  if (model) return model;
  return doc.spaces[0] ?? { entities: [] };
}

/** Options for {@link collectViewportContent}. */
export interface CollectViewportContentOptions {
  pageTransform: PdfTransform;
  ctx: CadConvertContext;
  layers: LayerVisibilityMap;
  /** Paper-space origin offset; defaults to {0,0}. */
  paperOrigin?: PaperOrigin;
}

/**
 * Walk the document's MODEL-space entities through one VIEWPORT, returning the
 * VP's visible model content (paths + text runs in RAW model coords), the
 * composed model→pt transform and the paper-mm clip rectangle.
 *
 * Skips entirely (empty content, `skipped=true`) when the VP is the control VP
 * or is off / degenerate.
 */
export function collectViewportContent(
  vp: ViewportEntity,
  doc: DrawingDocument,
  options: CollectViewportContentOptions,
): ViewportContent {
  const paperOrigin = options.paperOrigin ?? { x: 0, y: 0 };
  const transform = buildVpTransform(vp, options.pageTransform, paperOrigin);
  const clipRectMm = vpClipRectMm(vp, paperOrigin);

  const base: Omit<ViewportContent, "skipped" | "skipReason"> = {
    paths: [],
    textRuns: [],
    hatchPatterns: [],
    gradientHatches: [],
    clipRectMm,
    transform,
    // model→paper linear scale + twist; only meaningful for a rendered VP.
    unitToPaperScale: 0,
    twistDeg: 0,
  };

  if (isControlViewport(vp)) {
    return { ...base, skipped: true, skipReason: "control" };
  }
  if (isViewportOffOrDegenerate(vp)) {
    return { ...base, skipped: true, skipReason: "off" };
  }

  // Rendered VP: model→paper linear scale (height/viewHeight, both > 0 here per
  // isViewportOffOrDegenerate) + view-twist for the per-run text size/rotation.
  base.unitToPaperScale = vp.height / vp.viewHeight;
  base.twistDeg = vp.viewTwistAngle;

  const vpFrozen = new Set<string>(
    (vp.frozenLayers ?? []).map((n) => n.toLowerCase()),
  );

  const model = resolveModelSpace(doc);
  const paths: PathPrimitive[] = [];
  const textRuns: TextRunPrimitive[] = [];
  const hatchPatterns: HatchPatternPrimitive[] = [];
  const gradientHatches: GradientHatchPrimitive[] = [];

  for (const entity of model.entities) {
    if (!isEntityVisibleInVp(entity.layer, options.layers, vpFrozen)) continue;
    // Annotative-per-VP paper-sizing is PDF-3b ({@link AnnotativeSizingNote}).
    // The non-annotative path emits model geometry/text at the VP scale via the
    // composed transform like any other model content.
    for (const p of entityToPrimitives(entity, options.ctx)) {
      if (p.type === "path") paths.push(p);
      else if (p.type === "hatch-pattern") hatchPatterns.push(p);
      else if (p.type === "gradient-hatch") gradientHatches.push(p);
      else textRuns.push(p);
    }
  }

  return {
    ...base,
    paths,
    textRuns,
    hatchPatterns,
    gradientHatches,
    skipped: false,
    skipReason: null,
  };
}
